import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  DialogService,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';
import { AgrConnectFormDirective, AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { updatedDiff } from 'deep-object-diff';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  filter,
  map,
  skipWhile,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { FeedAdvice } from 'src/app/feed-advice/models/feed-advice.models';
import { getFeedAdvices } from 'src/app/feed-advice/store/feed-advice.selectors';
import { ReferenceValueSheet } from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import { getReferenceValueSheets } from 'src/app/reference-value-sheet/store/reference-value-sheet.selectors';

import { ObservationsRegistration } from 'src/app/within-cycle-performance/models/within-cycle-performance.model';
import { getObservations } from 'src/app/within-cycle-performance/store/selectors/within-cycle-performance.selectors';

import { getObservationSwitchSetting } from '../../../settings/store/selectors/setting.selector';
import { BeddingType, Concept, Cycle, WDOsettingEnum, WDOsettings } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getConcepts,
  getNextCycleNumber,
  getPreviousCycle,
  getSelectedCycle,
  getSelectedCycleId,
} from '../../store/selectors/cycle.selectors';
import { cycleNumberValidator } from '../../validators/cycle-number-validator';
import {
  WDOsettingsComponent,
  WDOsettingsDialogResultData,
} from '../wdo-settings/wdo-settings.component';

const defaultTransportDistance = 75;
const defaultTimeInFreeRange = 15;

@Component({
  selector: 'app-general',
  templateUrl: 'general.component.html',
  styleUrls: ['general.component.scss'],
  imports: [
    ReactiveFormsModule,
    AgrConnectFormDirective,
    MatFormField,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatSuffix,
    RouterLink,
    MatButton,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class GeneralComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly localeService = inject(LocaleService);
  private dialogService = inject(DialogService);

  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  concepts$: Observable<Concept[]> = this.store.select(getConcepts);
  refValSheets$: Observable<ReferenceValueSheet[]> = this.store
    .select(getReferenceValueSheets)
    .pipe(
      map((referenceValueSheets) =>
        referenceValueSheets.filter((referenceValueSheet) => referenceValueSheet.isActive),
      ),
    );
  feedAdvices$: Observable<FeedAdvice[]> = this.store
    .select(getFeedAdvices)
    .pipe(map((feedAdvices) => feedAdvices.filter((feedAdvice) => feedAdvice.isActive)));
  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));
  information$: Observable<Cycle> = this.cycle$.pipe(
    filter((val) => !!val && Object.entries(val).length > 0),
    take(1),
  );
  isEditingGeneral$: Observable<boolean> = this.store.pipe(
    select(getSelectedCycleId),
    map((val) => !!val),
  );

  concepts: Concept[] = [];

  // default settings in case user does not set them manually
  wdoSettings: WDOsettings = {
    mortality: WDOsettingEnum.PerDay,
    feedIntake: WDOsettingEnum.PerDay,
    waterIntake: WDOsettingEnum.PerDay,
    indoorTemperature: WDOsettingEnum.PerDay,
    outdoorTemperature: WDOsettingEnum.PerDay,
    indoorHumidity: WDOsettingEnum.PerDay,
    outdoorHumidity: WDOsettingEnum.PerDay,
    weight: WDOsettingEnum.PerDay,
    litterQuality: WDOsettingEnum.PerDay,
  };

  previousWDOsettings: WDOsettings;
  differencesWDOsettings: object;
  deleteChangedKPIData: boolean;
  beddingTypes = Object.values(BeddingType);

  subscription = new Subscription();
  unsubscribeSignal: Subject<void> = new Subject<void>();

  form: FormGroup = new FormGroup({
    cycleNumber: new FormControl(null, Validators.required, cycleNumberValidator(this.store)),
    concept: new FormControl(null, [Validators.required]),
    referenceValueSheetId: new FormControl(null),
    feedAdviceId: new FormControl(null),
    cycleDescription: new FormControl(null),
    beddingType: new FormControl<BeddingType>(null),
    beddingAmount: new FormControl<number>({ value: null, disabled: this.hasNoBeddingType() }, [
      Validators.min(0),
    ]),
    transportDistance: new FormControl<number>(75, [Validators.min(0), Validators.max(1000)]),
    timeInFreeRange: new FormControl<number>(15, [Validators.min(0), Validators.max(100)]),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    combineLatest([this.concepts$])
      .pipe(
        skipWhile(([concepts]) => concepts === undefined),
        take(1),
      )
      .subscribe(([concepts]) => {
        this.concepts = concepts;
      });
  }

  ngOnInit() {
    this.subscription.add(
      this.form.controls.beddingType.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal))
        .subscribe((beddingType: BeddingType) => {
          if (beddingType === BeddingType.NoBedding) {
            this.form.controls.beddingAmount.disable();
            this.form.controls.beddingAmount.setValue(null);
          } else {
            this.form.controls.beddingAmount.enable();
          }
        }),
    );

    this.store.dispatch(cycleActions.loadCyclesRequest());

    this.form.controls.concept.valueChanges.subscribe(() => {
      this.resetTimeInFreeRangeIfFreeRangeConceptSelected();
    });

    this.setInitialCycleValue();
  }

  compareConcept = (c1: Concept, c2: Concept) => c1 && c2 && c1.id === c2.id;

  setInitialCycleValue() {
    combineLatest([
      combineLatest([this.store.select(getNextCycleNumber), this.store.select(getPreviousCycle)]),
      combineLatest([
        this.routeUrl$,
        this.refValSheets$,
        this.feedAdvices$,
        this.cycle$,
        this.concepts$,
      ]),
    ]).subscribe(
      ([[nextCycleNumber, previousCycle], [url, refValSheets, feedAdvices, cycle, concepts]]) => {
        if (url.endsWith('/cycle/add')) {
          this.form.controls.cycleNumber.setValue(nextCycleNumber);
          if (!!previousCycle && !!previousCycle.concept) {
            const concept = concepts.find((x) => x.id === previousCycle.concept.id);
            if (concept) {
              this.form.controls.concept.patchValue(concept);
            }
          }
          if (!!previousCycle && !!previousCycle.referenceValueSheetId) {
            const refValSheet = refValSheets.find(
              (x) => x.id === previousCycle.referenceValueSheetId,
            );
            this.form.controls.referenceValueSheetId.patchValue(refValSheet?.id);
          }
          if (!!previousCycle && !!previousCycle.feedAdviceId) {
            const feedAdvice = feedAdvices.find((x) => x.id === previousCycle.feedAdviceId);
            this.form.controls.feedAdviceId.patchValue(feedAdvice?.id);
          }
        }

        if (cycle?.observationSettings) {
          this.wdoSettings = { ...cycle.observationSettings };
        }

        if (cycle?.referenceValueSheetId) {
          const refValSheet = refValSheets.find((x) => x.id === cycle?.referenceValueSheetId);
          this.form.controls.referenceValueSheetId.patchValue(refValSheet?.id);
        }

        if (cycle?.feedAdviceId) {
          const feedAdvice = feedAdvices.find((x) => x.id === cycle?.feedAdviceId);
          this.form.controls.feedAdviceId.patchValue(feedAdvice?.id);
        }

        this.form.controls.beddingType.patchValue(cycle?.beddingType);
        this.form.controls.beddingAmount.patchValue(cycle?.beddingAmount);
        this.form.controls.transportDistance.patchValue(
          cycle?.transportDistance ?? defaultTransportDistance,
        );
        this.form.controls.timeInFreeRange.patchValue(
          cycle?.timeInFreeRange ?? defaultTimeInFreeRange,
        );
      },
    );
  }

  private getCycle(cycle: Cycle) {
    let updatedConcept: Concept = cycle?.concept;

    if (this.form.controls.concept?.value) {
      updatedConcept = this.concepts.find((c) => c.id === this.form.controls.concept.value.id);
    }

    if (!updatedConcept && this.concepts.length > 0) {
      updatedConcept = this.concepts[0];
    }

    return {
      cycle: {
        ...cycle,
        ...this.form.value,
        observationSettings: this.wdoSettings,
        currency: cycle?.currency
          ? cycle.currency
          : this.localeService.getCustomerCurrency().toString(),
        id: cycle ? cycle.id : undefined,
        updatedConcept,
        beddingAmount: this.form.controls.beddingAmount?.value,
      },
    };
  }

  ngOnDestroy(): void {
    this.unsubscribeSignal.next();
    this.unsubscribeSignal.unsubscribe();
    this.subscription.unsubscribe();
  }

  shouldShowWDOsettings(): Observable<boolean> {
    return this.store.select(getObservationSwitchSetting).pipe(take(1));
  }

  openWDOsettings() {
    this.cycle$
      .pipe(
        withLatestFrom(this.store.select(getObservations), this.store.select(getPreviousCycle)),
        take(1),
        switchMap(([cycle, observations, previousCycle]) =>
          this.dialogService
            .form(WDOsettingsComponent, {
              cycle,
              setPendingChanges: false,
              confirmButton: true,
              confirmButtonText: this.localeService.translate('confirm'),
              settings: previousCycle ? previousCycle.observationSettings : this.wdoSettings,
              pushHistoryState: false,
            })
            .pipe(map((dialogResult) => [dialogResult, cycle, observations])),
        ),
      )
      .subscribe(
        ([dialogResult, cycle, observations]: [
          AbstractDialogResultData<WDOsettingsDialogResultData>,
          Cycle,
          ObservationsRegistration[],
        ]) => {
          if (dialogResult.status === DIALOG_RETURN_TYPES.confirm) {
            this.updateDifferenceForChangedKpi(this.wdoSettings, dialogResult.result?.settings);

            this.previousWDOsettings = this.wdoSettings;
            this.wdoSettings = { ...dialogResult.result?.settings };

            if (this.differencesWDOsettings && cycle) {
              this.showDeleteKPIDataDialog(observations);
            }
          }
        },
      );
  }

  save() {
    if (this.form.valid) {
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        if (cycle) {
          this.store.dispatch(cycleActions.upsertCycleGeneral(this.getCycle(cycle)));
        } else {
          this.store.dispatch(cycleActions.createCycle(this.getCycle(cycle)));
        }
      });
    }
  }

  private updateDifferenceForChangedKpi(oldWDOsettings: WDOsettings, newWDOsettings: WDOsettings) {
    this.differencesWDOsettings = updatedDiff(oldWDOsettings, newWDOsettings);
  }

  private showDeleteKPIDataDialog(observations: ObservationsRegistration[]) {
    const changedWdoLabels: string[] = [];

    Object.keys(this.differencesWDOsettings).forEach((wdoSetting) => {
      const changedWdoLabel = this.localeService.translate('wdo-labels.' + wdoSetting);

      switch (wdoSetting) {
        case 'mortality':
          observations.forEach((element) => {
            if (element.mortality && !changedWdoLabels.includes(changedWdoLabel)) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'feedIntake':
          observations.forEach((element) => {
            if (element.feedIntakeInKg && !changedWdoLabels.includes(changedWdoLabel)) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'waterIntake':
          observations.forEach((element) => {
            if (element.waterIntakeInL && !changedWdoLabels.includes(changedWdoLabel)) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'indoorTemperature':
          observations.forEach((element) => {
            if (element.indoorTemperatureInCelsius && !changedWdoLabels.includes(changedWdoLabel)) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'outdoorTemperature':
          observations.forEach((element) => {
            if (
              element.outdoorTemperatureInCelsius &&
              !changedWdoLabels.includes(changedWdoLabel)
            ) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'indoorHumidity':
          observations.forEach((element) => {
            if (
              element.indoorHumidityInPercentages &&
              !changedWdoLabels.includes(changedWdoLabel)
            ) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'outdoorHumidity':
          observations.forEach((element) => {
            if (
              element.outdoorHumidityInPercentages &&
              !changedWdoLabels.includes(changedWdoLabel)
            ) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'weight':
          observations.forEach((element) => {
            if (element.chickWeightInGrams && !changedWdoLabels.includes(changedWdoLabel)) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;
        case 'litterQuality':
          observations.forEach((element) => {
            if (
              (element.litterQualityCakedPercentage ||
                element.litterQualityLoosePercentage ||
                element.litterQualityWetPercentage) &&
              !changedWdoLabels.includes(changedWdoLabel)
            ) {
              changedWdoLabels.push(changedWdoLabel);
            }
          });
          break;

        default:
          break;
      }
    });

    if (changedWdoLabels.length > 0) {
      this.dialogService
        .confirmWarning({
          title: this.localeService.translate('wdo-settings.delete-confirm.title'),
          text:
            this.localeService.translate('wdo-settings.delete-confirm.text') +
            changedWdoLabels.join(', '),
          pushHistoryState: false,
        })
        .pipe(
          tap((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.deleteChangedKPIData = true;
            } else {
              this.deleteChangedKPIData = false;
              this.wdoSettings = { ...this.previousWDOsettings };
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  private hasNoBeddingType(): boolean {
    const beddingType = this.form?.get('beddingType')?.value;
    return beddingType === BeddingType.NoBedding;
  }

  private resetTimeInFreeRangeIfFreeRangeConceptSelected(): void {
    if (this.isFreeRange()) {
      const currentValue = this.form.controls.timeInFreeRange.value;
      if (currentValue == null || currentValue === 0) {
        this.form.controls.timeInFreeRange.setValue(15);
      }
    }
  }

  isFreeRange(): boolean {
    const selectedConceptName = this.form.get('concept')?.value?.name?.toLowerCase();

    return selectedConceptName === 'scharrel' || selectedConceptName === 'blk 1 ster vleeswaren';
  }
}
